import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronDown, Phone, BarChart3, Users, FileText, Megaphone, Building2, Stethoscope, Scale, Home, Sparkles, ShoppingBag, BookOpen, HelpCircle, Newspaper, Menu, X, Circle, Ticket, Package, Brain, PhoneForwarded, Shield, CreditCard } from 'lucide-react';

const DROPDOWN_ITEMS = {
  Products: [
    { icon: Phone, label: 'AI Receptionist', desc: '24/7 call answering & appointment booking', href: '#features' },
    { icon: Users, label: 'CRM', desc: 'Contact management & lead pipeline', href: '#features' },
    { icon: Megaphone, label: 'Sales Outreach', desc: 'AI-powered campaign automation', href: '#features' },
    { icon: FileText, label: 'Invoicing', desc: 'Create, send & track invoices', href: '#features' },
    { icon: Ticket, label: 'Ticketing System', desc: 'AI-generated support tickets', href: '#features' },
    { icon: BarChart3, label: 'Conversation Insights', desc: 'Call analytics & reporting', href: '#features' },
  ],
  Features: [
    { icon: PhoneForwarded, label: 'Call Forwarding', desc: '25+ carrier setup guides', href: '#features' },
    { icon: Shield, label: 'FAQ Manager', desc: 'Train AI on your business', href: '#features' },
    { icon: Package, label: 'Products Catalog', desc: 'Services & pricing management', href: '#features' },
    { icon: Brain, label: 'AI Onboarding', desc: 'Automated business profile setup', href: '#features' },
    { icon: CreditCard, label: 'Payment Settings', desc: 'Bank details & payment links', href: '#features' },
    { icon: Users, label: 'Team Management', desc: 'Multi-user roles & access', href: '#features' },
  ],
  Solutions: [
    { icon: Stethoscope, label: 'Healthcare', desc: 'Patient scheduling & intake', href: '#features' },
    { icon: Scale, label: 'Legal Services', desc: 'Client intake & qualification', href: '#features' },
    { icon: Home, label: 'Home Services', desc: 'Appointment booking & dispatch', href: '#features' },
    { icon: Sparkles, label: 'Beauty & Wellness', desc: 'Salon & spa scheduling', href: '#features' },
    { icon: Building2, label: 'Real Estate', desc: 'Lead capture & showings', href: '#features' },
    { icon: ShoppingBag, label: 'Retail & eCommerce', desc: 'Order inquiries & support', href: '#features' },
  ],
};

const DropdownMenu = ({ items, isOpen }) => {
  if (!isOpen) return null;
  const wide = items.length > 3;
  return (
    <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-[#1a1f35]/95 backdrop-blur-xl rounded-xl shadow-2xl shadow-black/40 border border-white/10 overflow-hidden ${wide ? 'w-[520px]' : 'w-[280px]'}`} data-testid="dropdown-menu">
      <div className={`p-2 ${wide ? 'grid grid-cols-2 gap-0.5' : ''}`}>
        {items.map((item) => (
          <a key={item.label} href={item.href} className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group" data-testid={`dropdown-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/20 transition-colors">
              <item.icon className="w-4 h-4 text-purple-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors">{item.label}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (name) => {
    clearTimeout(timeoutRef.current);
    setOpenDropdown(name);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const navItems = ['Products', 'Features', 'Solutions'];

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 pt-4">
        <div className="flex items-center justify-between h-14 px-6 bg-[#0d1224]/80 backdrop-blur-xl rounded-2xl border border-white/[0.06] shadow-lg shadow-black/20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="header-logo">
            <img src="/logo-mark.png" alt="Miadesk" className="h-7 w-auto" />
            <span className="text-white font-medium text-base">
              Miadesk <span className="text-gray-500">&mdash;</span> <span className="text-gray-400 font-normal">AI Agent</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((name) => (
              <div
                key={name}
                className="relative"
                onMouseEnter={() => handleMouseEnter(name)}
                onMouseLeave={handleMouseLeave}
              >
                <button className={`flex items-center gap-1 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${openDropdown === name ? 'text-white bg-white/5' : 'text-gray-400 hover:text-gray-200'}`} data-testid={`nav-${name.toLowerCase()}`}>
                  {name}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === name ? 'rotate-180' : ''}`} />
                </button>
                <DropdownMenu items={DROPDOWN_ITEMS[name]} isOpen={openDropdown === name} />
              </div>
            ))}
            <a href="#pricing" className="px-3.5 py-1.5 text-sm font-medium text-gray-400 hover:text-gray-200 rounded-lg transition-colors" data-testid="nav-pricing">
              Pricing
            </a>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3" data-testid="header-cta">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white text-sm font-medium rounded-full transition-all shadow-lg shadow-purple-500/25" data-testid="header-dashboard-btn">
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="px-4 py-1.5 text-sm font-medium text-gray-400 hover:text-white transition-colors" data-testid="header-login-btn">
                  Log in
                </button>
                <button onClick={() => navigate('/register')} className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white text-sm font-medium rounded-full transition-all shadow-lg shadow-purple-500/25" data-testid="header-start-free-btn">
                  <Circle className="w-3 h-3 fill-white" />
                  Start Free Trial
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-gray-400 hover:text-white" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden mx-6 mt-2 bg-[#0d1224]/95 backdrop-blur-xl border border-white/[0.06] rounded-2xl px-5 py-4 space-y-4 shadow-2xl shadow-black/40" data-testid="mobile-menu">
          {navItems.map((name) => (
            <div key={name} className="space-y-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{name}</p>
              {DROPDOWN_ITEMS[name].map((item) => (
                <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 text-sm text-gray-300 hover:text-white">
                  <item.icon className="w-4 h-4 text-purple-400" />
                  {item.label}
                </a>
              ))}
            </div>
          ))}
          <a href="#pricing" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-gray-300 hover:text-white">Pricing</a>
          <div className="flex gap-3 pt-2 border-t border-white/5">
            {user ? (
              <button onClick={() => { setMobileOpen(false); navigate('/dashboard'); }} className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-sm font-medium rounded-full text-center">Dashboard</button>
            ) : (
              <>
                <button onClick={() => { setMobileOpen(false); navigate('/register'); }} className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-sm font-medium rounded-full text-center">Start Free Trial</button>
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} className="flex-1 py-2.5 text-gray-400 text-sm font-medium text-center hover:text-white">Log in</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
